/**
 * Discount System API Client
 * Phase 3 Frontend - Type-safe API wrapper for 15 endpoints
 */

import { apiClient } from '../services/api';
import {
  VariableDiscountSetting,
  CustomDiscount,
  CreateCustomDiscountInput,
  UpdateCustomDiscountInput,
  UpdateVariableDiscountSettingsInput,
  DiscountPreview,
  DiscountAuditLog,
  InvoiceDiscountDetails,
  DiscountStatistics,
  CustomDiscountsListResponse,
  GetCustomDiscountsParams,
  GetStatsParams,
  ApproveDiscountsInput,
  ApplyOneOffDiscountInput,
  RemoveDiscountInput,
} from '../types/discount.types';

// API Response wrapper type
interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ============================================================================
// VARIABLE DISCOUNT SETTINGS (2 endpoints)
// ============================================================================

/**
 * Get variable discount settings for the current school
 */
export async function getVariableDiscountSettings(): Promise<VariableDiscountSetting> {
  const response = await apiClient.get<ApiResponse<VariableDiscountSetting>>(
    '/admin/discounts/settings/variable'
  );
  return response.data;
}

/**
 * Update variable discount settings
 */
export async function updateVariableDiscountSettings(
  data: UpdateVariableDiscountSettingsInput
): Promise<VariableDiscountSetting> {
  const response = await apiClient.patch<ApiResponse<VariableDiscountSetting>>(
    '/admin/discounts/settings/variable',
    data
  );
  return response.data;
}

// ============================================================================
// CUSTOM DISCOUNTS CRUD (5 endpoints)
// ============================================================================

/**
 * Get paginated list of custom discounts with filters
 */
export async function getCustomDiscounts(
  params: GetCustomDiscountsParams
): Promise<CustomDiscountsListResponse> {
  const response = await apiClient.get<ApiResponse<CustomDiscountsListResponse>>(
    '/admin/discounts',
    { params }
  );
  return response.data;
}

/**
 * Create a new custom discount
 */
export async function createCustomDiscount(
  data: CreateCustomDiscountInput
): Promise<CustomDiscount> {
  const response = await apiClient.post<ApiResponse<CustomDiscount>>(
    '/admin/discounts',
    data
  );
  return response.data;
}

/**
 * Get a specific custom discount by ID
 */
export async function getCustomDiscountById(id: string): Promise<CustomDiscount> {
  const response = await apiClient.get<ApiResponse<CustomDiscount>>(
    `/admin/discounts/${id}`
  );
  return response.data;
}

/**
 * Update an existing custom discount
 */
export async function updateCustomDiscount(
  id: string,
  data: UpdateCustomDiscountInput
): Promise<CustomDiscount> {
  const response = await apiClient.patch<ApiResponse<CustomDiscount>>(
    `/admin/discounts/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete a custom discount
 */
export async function deleteCustomDiscount(id: string): Promise<void> {
  await apiClient.delete(`/admin/discounts/${id}`);
}

// ============================================================================
// INVOICE DISCOUNT APPROVAL (5 endpoints)
// ============================================================================

/**
 * Get available discounts and preview for an invoice
 */
export async function getDiscountPreview(invoiceId: string): Promise<DiscountPreview> {
  const response = await apiClient.get<ApiResponse<DiscountPreview>>(
    `/admin/discounts/invoice/${invoiceId}/preview`
  );
  return response.data;
}

/**
 * Approve and apply selected discounts to an invoice
 */
export async function approveDiscounts(
  invoiceId: string,
  data: ApproveDiscountsInput
): Promise<any> {
  const response = await apiClient.post<ApiResponse<any>>(
    `/admin/discounts/invoice/${invoiceId}/approve`,
    data
  );
  return response.data;
}

/**
 * Apply a one-off, admin-typed discount to a DRAFT invoice.
 */
export async function applyOneOffDiscount(
  invoiceId: string,
  data: ApplyOneOffDiscountInput
): Promise<unknown> {
  const response = await apiClient.post<ApiResponse<unknown>>(
    `/admin/discounts/invoice/${invoiceId}/one-off`,
    data
  );
  return response.data;
}

/**
 * Get discount audit trail for an invoice
 */
export async function getDiscountAuditTrail(invoiceId: string): Promise<DiscountAuditLog[]> {
  const response = await apiClient.get<ApiResponse<DiscountAuditLog[]>>(
    `/admin/discounts/invoice/${invoiceId}/audit`
  );
  return response.data;
}

/**
 * Remove a discount from an invoice with reason
 */
export async function removeDiscount(
  invoiceId: string,
  discountId: string,
  data: RemoveDiscountInput
): Promise<any> {
  const response = await apiClient.delete<ApiResponse<any>>(
    `/admin/discounts/invoice/${invoiceId}/discount/${discountId}`,
    { data }
  );
  return response.data;
}

/**
 * Recalculate discounts for an invoice
 */
export async function recalculateDiscounts(invoiceId: string): Promise<any> {
  const response = await apiClient.post<ApiResponse<any>>(
    `/admin/discounts/invoice/${invoiceId}/recalculate`,
    {}
  );
  return response.data;
}

// ============================================================================
// REPORTING (3 endpoints)
// ============================================================================

/**
 * Get discount statistics for date range
 */
export async function getDiscountStats(params?: GetStatsParams): Promise<DiscountStatistics> {
  const response = await apiClient.get<ApiResponse<DiscountStatistics>>(
    '/admin/discounts/stats',
    { params }
  );
  return response.data;
}

/**
 * Get invoices with pending discount approvals
 */
export async function getPendingApprovals(): Promise<any[]> {
  const response = await apiClient.get<ApiResponse<any[]>>(
    '/admin/discounts/pending-approvals'
  );
  return response.data;
}

/**
 * Get detailed discount information for an invoice
 */
export async function getInvoiceDiscountDetails(invoiceId: string): Promise<InvoiceDiscountDetails> {
  const response = await apiClient.get<ApiResponse<InvoiceDiscountDetails>>(
    `/admin/discounts/invoice/${invoiceId}/details`
  );
  return response.data;
}

/**
 * Get discount eligibility for families in a term (before invoice generation)
 */
export async function getTermDiscountEligibility(termId: string): Promise<any[]> {
  const response = await apiClient.get<ApiResponse<any[]>>(
    `/admin/discounts/term/${termId}/eligibility`
  );
  return response.data;
}
